import React, { useState, useEffect, useRef } from 'react';
import MonthDayEvent from "./MonthDayEvent";
import WeekView from './WeekView';
import featureFlags from '../config/featureFlags';

const MonthDays = ({ curMonth, curYear, events, onModalStateChange, selectedEventSlug, onModalClose, isModalOpen, theme }) => {
    const dayRefs = useRef([]);
    const closeButtonRef = useRef(null);
    const [modalWeekIdx, setModalWeekIdx] = useState(null);

    const firstDay = new Date(curYear, curMonth-1, 1).getDay();
    const lastDay = new Date(curYear, curMonth, 0).getDate();

    const curMonthName = new Date(curYear, curMonth-1, 1).toLocaleString('default', { month: 'long' })
  
    // create an array of days in the month
    var numberofdays = 35;

    if (lastDay+firstDay > 35) { numberofdays = 42; }

    var daysOfMonth = Array.from({length: numberofdays}, (e,i) => i+1 );

    // Group days into weeks (array of arrays)
    const weeks = [];
    for (let i = 0; i < daysOfMonth.length; i += 7) {
        weeks.push(daysOfMonth.slice(i, i + 7));
    }

    // Handle keyboard events for modals
    const handleKeyDown = (e, closeFunction) => {
        if (e.key === 'Escape') {
            handleCloseModal();
        }
    };

    const handleCloseModal = () => {
        setModalWeekIdx(null);
    }       

    const handleOpenModal = (weekIdx) => {
        setModalWeekIdx(weekIdx);
        isModalOpen = true;
    }
    // Notify parent when modal state changes
    useEffect(() => {
        if (onModalStateChange) {
            onModalStateChange(modalWeekIdx !== null);
        }
    }, [modalWeekIdx, onModalStateChange]);

    // Close modal when parent indicates it should be closed
    useEffect(() => {
        if (!isModalOpen) {
            handleCloseModal();
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (modalWeekIdx !== null && closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
    }, [modalWeekIdx]);

    const getDateForCell = (index) => new Date(curYear, curMonth-1, index-firstDay);

    // Helper to get events for a week
    const getEventsForWeek = (week) => {
        const weekDates = week.map(idx => getDateForCell(idx));
        const weekDateStrs = weekDates.map(d => d.toISOString().split('T')[0]);
        return events.filter(e => weekDateStrs.includes(e.startDate.split('T')[0]));
    };

    // Helper to get Date objects for a week
    const getDatesForWeek = (week) => week.map(idx => getDateForCell(idx));

    return (
      <div className={`rounded-lg shadow-md p-4 w-full mt-2 ${theme.gridBg} opacity-[.93] p-2 pr-5 pl-5`} role="region" aria-label={`Calendar for ${curMonthName} ${curYear}`}>      
        <div className={`${theme.alertBackground} rounded-lg p-3 mb-4 text-center sm:text-left`} role="alert">
                Note: Events are subject to change. <br className="block sm:hidden"/> Please verify with the event host.
        </div>
        <div className="overflow-x-auto">
        <div className={`grid ${featureFlags.weekViewButton?.enabled !== false ? 'grid-cols-[30px_repeat(7,1fr)]' : 'grid-cols-7'} ${theme.gridBg} divide-x divide-y ${theme.divider}`} role="grid" aria-label={`Days in ${curMonthName} ${curYear}`}> 
            {/* Header row: empty for icon, then day names */}
            {featureFlags.weekViewButton?.enabled !== false && (
                
            <div className='w-10'></div>
            )}
            <div className={`flex align-middle justify-center font-bold ${theme.weekendHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Sunday</span><span className='block md:hidden'>S</span></div>
            <div className={`flex align-middle justify-center font-bold ${theme.weekdayHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Monday</span><span className='block md:hidden'>M</span></div>
            <div className={`flex align-middle justify-center font-bold ${theme.weekdayHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Tuesday</span><span className='block md:hidden'>T</span></div>
            <div className={`flex align-middle justify-center font-bold ${theme.weekdayHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Wednesday</span><span className='block md:hidden'>W</span></div> 
            <div className={`flex align-middle justify-center font-bold ${theme.weekdayHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Thursday</span><span className='block md:hidden'>T</span></div>
            <div className={`flex align-middle justify-center font-bold ${theme.weekdayHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Friday</span><span className='block md:hidden'>F</span></div>    
            <div className={`flex align-middle justify-center font-bold ${theme.weekendHeaderBg} ${theme.dayNameText} p-2`} role="columnheader"><span className='hidden md:block'>Saturday</span><span className='block md:hidden'>S</span></div>  
            {/* Render weeks */}
            {weeks.map((week, weekIdx) => (
                <React.Fragment key={weekIdx}>
                    {/* Icon cell */}
                    {featureFlags.weekViewButton?.enabled !== false && (
                    <div className={`flex items-center justify-center border-r border-b ${theme.todayBarBackground}`}>
                          <button
                              onClick={() => handleOpenModal(weekIdx)}
                              className={`h-6 w-6 flex items-center justify-center rounded-full hover:bg-blue-300 transition-colors ${theme.button} p-0`}
                              aria-label={'Show week view'}
                          >
                              <i className="fas fa-calendar-week"></i>
                          </button>
                    </div>
                        )}
                    {/* 7 days of the week */}
                    {week.map((index, dayIdx) => {
                        if (index-firstDay < 1 || index-firstDay > lastDay) {
                            return (               
                                <div key={"blankday" + index + weekIdx} className={`flex align-middle justify-center p-2 h-22 lg:h-40 border-b`} role="gridcell" aria-hidden="true">
                                </div>
                            )
                        } else {
                            var thisDate = getDateForCell(index);
                            var thisDayEvents = events.filter(event => event.startDate.split('T')[0] === thisDate.toISOString().split('T')[0]);
                            var todayDate = new Date();
                            var isToday = thisDate.toLocaleDateString() === todayDate.toLocaleDateString();
                            var dayClasses = `${theme.dayBg} align-middle justify-left opacity-100 overflow-y-auto transition-shadow duration-200 hover:z-10 hover:shadow-lg`;
                            let todayBorder = '';
                            if (isToday) {
                                dayClasses += ` ${theme.todayBg}`;
                                // Use theme.todayBorder if provided, else fallback
                                todayBorder = theme.todayBorder || (theme.isDark ? 'border-white' : 'border-gray-900');
                            }
                            // if the day has featured events, add the featuredDay class
                            
                            let isFeaturedDay = false;
                            if (thisDayEvents.some(event => event.eventType === 'Featured')) {
                                isFeaturedDay = true;
                            }

                            return (
                                <div 
                                    key={"day" + index + weekIdx} 
                                    className={`${dayClasses} lg:p-0 lg:pb-4 h-22 lg:h-40 overflow-y-hidden border-b`}
                                    role="gridcell"
                                    aria-label={`${thisDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${thisDayEvents.length > 0 ? `, ${thisDayEvents.length} event${thisDayEvents.length > 1 ? 's' : ''}` : ''}`}
                                >
                                    <p onClick={() => {
                                        if (dayRefs.current[index]) {
                                            dayRefs.current[index].openDayModal();
                                        }
                                    }} className={`${isFeaturedDay ? theme.featuredDay : isToday ? theme.todayBarBackground : theme.dayHeaderBg} font-medium cursor-pointer items-center justify-center flex`}>
                                        
                                        <span className={`block w-8 h-6 rounded-full ${isToday ? theme.todayBackground : ''}`}>
                                            {index-firstDay}                                          
                                        </span>
                                        <span className='block'>{isFeaturedDay && <i className={`text-xs fas fa-star ${theme.text}`} aria-hidden="true"></i>}</span>
                                    </p>
                                    <MonthDayEvent 
                                        ref={el => dayRefs.current[index] = el}
                                        currentdate={thisDate} 
                                        events={thisDayEvents} 
                                        currentDay={index-firstDay}
                                        onModalStateChange={onModalStateChange}
                                        selectedEventSlug={selectedEventSlug}
                                        onModalClose={onModalClose}
                                        isModalOpen={isModalOpen}
                                        theme={theme}
                                    />
                                </div>  
                            )
                        }
                    })}
                </React.Fragment>
            ))}
        </div>
        </div>
        {/* WeekView Modal */}
        {modalWeekIdx !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" role="dialog" aria-modal="true" onClick={() => setModalWeekIdx(null)}>

            <div className="bg-white rounded-lg shadow-lg max-w-5xl w-full pr-4 pl-4 pb-4 relative" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold">Week of {curMonthName} {weeks[modalWeekIdx][0]} - {weeks[modalWeekIdx][6]}</h2>
                    <button
                        ref={closeButtonRef}
                        onClick={() => handleCloseModal()}
                        onKeyDown={(e) => handleKeyDown(e, handleCloseModal)}
                        className="text-gray-500 hover:text-gray-800 text-2xl font-bold p-2"
                        aria-label="Close week view"
                    >
                        &times;
                    </button>
             </div>
              <WeekView
                days={getDatesForWeek(weeks[modalWeekIdx])}
                events={getEventsForWeek(weeks[modalWeekIdx])}
                theme={theme}
              />
            </div>
          </div>
        )}
      </div>
    );
};

export default MonthDays;
